import { Input } from 'antd'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SuffixIcon } from '../../../modules/searchResults/components/SuffixIcon'
import { KeyResultDto, ObjectiveKeyResultDto, ObjectiveTemplateDto } from '../../../types'
import PerButton from '../Button'
import PerButtonCircle from '../ButtonCircle'
import PerFlexRow from '../FlexRow'
import PerSpacer from '../Spacer'
import PerText from '../Text'
import React from 'react'

export type TargetsTemplateSelectProps = {
  targetsTemplate: ObjectiveTemplateDto[]
  setTitle: (name: 'title', value: string) => void
  clearErrors: (value) => void
  replaceTasks: (value) => void
}

export const TargetsTemplateSelect: FC<TargetsTemplateSelectProps> = ({ targetsTemplate, setTitle, clearErrors, replaceTasks }) => {
  const { t } = useTranslation()
  const [templateBoxVisible, toggleTemplateBoxVisible] = useState<boolean>(false)

  const [searchTerm, setSearchTerm] = useState('')

  const optionsToDisplay = useMemo(() => {
    if (searchTerm) {
      return targetsTemplate.filter((t) => t.objectiveTitle.toLowerCase().includes(searchTerm.toLowerCase()))
    }
    return targetsTemplate
  }, [searchTerm, targetsTemplate])

  const handleClick = (target: ObjectiveTemplateDto) => {
    const tasks: KeyResultDto[] = target.objectiveKeyResults.map((t: ObjectiveKeyResultDto): KeyResultDto => {
      // When replacing existing key results, the values of all default keys must be reset
      return { completed: false, text: t.text }
    })
    setTitle('title', target.objectiveTitle)
    replaceTasks(tasks)
    clearErrors(['title', 'keyResults'])
    toggleTemplateBoxVisible(!templateBoxVisible)
  }

  return (
    <>
      <PerButton
        text={t('appraisalMeeting.targetsTemplate.chooseFromTemplate')}
        color="success"
        variant="pastel"
        before={!!templateBoxVisible ? 'remove_stroke' : 'add_stroke'}
        type="button"
        disabled={targetsTemplate.length === 0}
        tooltip={targetsTemplate.length === 0 && t('appraisalMeeting.targetsTemplate.chooseFromTemplateTooltip')}
        tooltipPlacement="right"
        onClick={() => toggleTemplateBoxVisible(!templateBoxVisible)}
        mt="xxxs"
      />

      <div className={templateBoxVisible ? 'target-templates-box expanding' : 'target-templates-box shrinking'}>
        <div className="target-templates-input">
          <Input
            type="text"
            name="searchTerm"
            value={searchTerm}
            onChange={({ target: { value } }) => setSearchTerm(value)}
            placeholder={t('appraisalMeeting.targetsTemplate.chooseFromTemplatePlaceholder')}
            suffix={
              <>
                {searchTerm && <PerButtonCircle size="small" icon="close_stroke" color="secondary" onClick={() => setSearchTerm('')} />}
                <SuffixIcon loading={false} />
              </>
            }
          />
        </div>

        <div style={{ maxHeight: '280px', overflow: 'auto' }}>
          {optionsToDisplay.length !== 0 ? (
            optionsToDisplay.map((target, index) => {
              return (
                <React.Fragment key={index}>
                  <div className="target-templates-option-padding" onClick={() => handleClick(target)}>
                    <PerFlexRow key={`${target.id}_${index}`} className="fullWidth" noWrap direction="column">
                      <div className="target-template-option">
                        <PerText mb="xxs" mt="xxs">
                          {target.objectiveTitle}
                        </PerText>
                      </div>
                    </PerFlexRow>
                  </div>
                </React.Fragment>
              )
            })
          ) : (
            <PerFlexRow key={`no-templates-found`} className="fullWidth" noWrap direction="column">
              <div className="no-templates-found">
                <PerText color="secondary">{t('appraisalMeeting.targetsTemplate.templatesNotFound')}</PerText>
              </div>
            </PerFlexRow>
          )}
          <PerSpacer mb="s" />
        </div>
      </div>
      {templateBoxVisible && <PerSpacer mb="l" />}
    </>
  )
}
